<template>
    <v-row justify="center">
        <v-dialog
            v-model="isOpen"
            @click:outside="closeWhenNoChanges()"
            @keydown.esc="closeWhenNoChanges()"
            persistent
            max-width="600px"
        >
            <v-card>
                <v-form>
                    <v-card-title>
                        <span class="headline">{{
                            formData.id
                                ? $t('createOrUpdateContract.editTitle')
                                : $t('createOrUpdateContract.createTitle')
                        }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-container>
                            <v-row>
                                <v-col cols="12">
                                    <v-autocomplete
                                        v-model="employee"
                                        :items="employees"
                                        :loading="isLoading"
                                        :label="
                                            $t(
                                                'createOrUpdateContract.forms.employee'
                                            )
                                        "
                                        :error-messages="userIdErrors"
                                        hide-no-data
                                        hide-selected
                                        placeholder="Start typing to Search"
                                        item-text="fullName"
                                        item-value="id"
                                        return-object
                                        autofocus
                                        clearable
                                        @update:search-input="
                                            onUpdateEmployeeSearch
                                        "
                                        @change="onChangeEmployee"
                                        @input="$v.formData.userId.$touch()"
                                        @blur="$v.formData.userId.$touch()"
                                        @keyup="clearServerErrors('userId')"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="formData.position"
                                        :label="
                                            $t(
                                                'createOrUpdateContract.forms.position'
                                            )
                                        "
                                        type="string"
                                        :error-messages="positionErrors"
                                        @input="$v.formData.position.$touch()"
                                        @blur="$v.formData.position.$touch()"
                                        @keyup="clearServerErrors('position')"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-menu
                                        v-model="isStartDatePickerVisible"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                :label="
                                                    $t(
                                                        'createOrUpdateContract.forms.startDate'
                                                    )
                                                "
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                :value="
                                                    formData.startDate
                                                        | dateFormatFromSettings
                                                "
                                                :error-messages="
                                                    startDateErrors
                                                "
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="formData.startDate"
                                            no-title
                                            scrollable
                                            @input="onStartDatePickerInput"
                                        />
                                    </v-menu>
                                </v-col>
                                <v-col
                                    cols="12"
                                    sm="6"
                                    class="d-flex justify-space-between align-center"
                                >
                                    <v-menu
                                        v-model="isEndDatePickerVisible"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                :label="
                                                    $t(
                                                        'createOrUpdateContract.forms.endDate'
                                                    )
                                                "
                                                :disabled="endDateFieldDisabled"
                                                :readonly="endDateFieldDisabled"
                                                prepend-icon="mdi-calendar"
                                                :value="
                                                    formData.endDate
                                                        | dateFormatFromSettings
                                                "
                                                @input="
                                                    $v.formData.endDate.$touch(),
                                                        onChangeDate()
                                                "
                                                @blur="
                                                    $v.formData.endDate.$touch()
                                                "
                                                :error-messages="endDateErrors"
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="formData.endDate"
                                            no-title
                                            scrollable
                                            @input="onEndDatePickerInput"
                                        />
                                    </v-menu>
                                    <div v-show="endDateFieldDisabled">
                                        <v-icon
                                            size="25"
                                            style="height: 25px"
                                            class="d-flex mx-3"
                                            @click="
                                                endDateFieldDisabled = !endDateFieldDisabled
                                            "
                                        >
                                            mdi-pencil
                                        </v-icon>
                                    </div>
                                </v-col>
                                <v-col
                                    cols="12"
                                    sm="6"
                                    class="d-flex justify-space-between align-center"
                                >
                                    <v-text-field
                                        v-model="formData.duration"
                                        :disabled="!endDateFieldDisabled"
                                        :readonly="!endDateFieldDisabled"
                                        type="number"
                                        :label="
                                            $t(
                                                'createOrUpdateContract.forms.duration'
                                            )
                                        "
                                        :error-messages="durationErrors"
                                        @input="
                                            $v.formData.duration.$touch(),
                                                onChangeDate()
                                        "
                                        @blur="$v.formData.duration.$touch()"
                                        @keyup="clearServerErrors('duration')"
                                    />
                                    <div v-show="!endDateFieldDisabled">
                                        <v-icon
                                            size="25"
                                            style="height: 25px"
                                            class="d-flex mx-3"
                                            @click="
                                                endDateFieldDisabled = !endDateFieldDisabled
                                            "
                                        >
                                            mdi-pencil
                                        </v-icon>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-select
                                        v-model="formData.daysOff"
                                        :items="[20, 26]"
                                        :label="
                                            $t(
                                                'createOrUpdateContract.forms.daysOff'
                                            )
                                        "
                                        hint="vacations days per year"
                                        required
                                        :error-messages="daysOffErrors"
                                        @input="$v.formData.daysOff.$touch()"
                                        @blur="$v.formData.daysOff.$touch()"
                                        @keyup="clearServerErrors('daysOff')"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="red darken-1" text @click="onClose">{{
                            $t('createOrUpdateContract.forms.close')
                        }}</v-btn>
                        <v-btn color="blue darken-1" text @click="onSave">{{
                            $t('createOrUpdateContract.forms.save')
                        }}</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions } from 'vuex';
import createOrEditValidator from '@/validators/contracts/createOrEditValidator.mixin';
import calculateDuration from '@/helpers/calculateDuration';

export default {
    name: 'createOrUpdateContract',
    props: {
        isOpen: Boolean,
        contract: {
            type: Object,
            default: () => {
                return {};
            }
        }
    },
    mixins: [createOrEditValidator],
    data() {
        return {
            employee: {},
            searchedEmployees: [],
            defaultFormData: {
                userId: '',
                position: '',
                startDate: dayjs().format('YYYY-MM-DD'),
                endDate: '',
                duration: '',
                daysOff: ''
            },
            formData: {},
            formDataCopy: {},
            isStartDatePickerVisible: false,
            isEndDatePickerVisible: false,
            isLoading: false,
            endDateFieldDisabled: true,
            errorMessage: '',
            changed: false
        };
    },
    computed: {
        employees() {
            return this.searchedEmployees.map(e => {
                return {
                    id: e.id,
                    fullName: e.firstName + ' ' + e.lastName
                };
            });
        }
    },
    watch: {
        contract() {
            this.setDefaultData();

            if (!this.contract) {
                return;
            }

            this.formData = {
                ...this.contract
            };

            this.formDataCopy = {
                ...this.formData
            };

            const { user } = this.contract;

            if (user) {
                this.employee = {
                    id: user.id,
                    fullName: user.firstName + ' ' + user.lastName
                };
                this.searchedEmployees.push(user);
            }
        }
    },
    created() {
        this.formData = { ...this.defaultFormData };
    },
    methods: {
        ...mapActions({
            searchUsers: 'users/search',
            saveContract: 'contracts/save'
        }),
        setDefaultData() {
            this.formData = { ...this.defaultFormData };
            this.formDataCopy = { ...this.formData };
            this.employee = {};
            this.searchedEmployees = [];
            this.errorMessage = '';
        },
        async onUpdateEmployeeSearch(query) {
            if (!query || this.isLoading) return;
            if (this.employee && this.employee.fullName === query) return;

            this.isLoading = true;
            this.searchedEmployees = await this.searchUsers(query);
            this.isLoading = false;
        },
        onChangeEmployee(employee) {
            this.formData.userId = employee
                ? employee.id
                : this.defaultFormData.userId;
        },
        onChangeDate() {
            const date = dayjs(this.formData.startDate);

            if (this.endDateFieldDisabled) {
                if (!this.formData.duration) {
                    return;
                }

                this.formData.endDate = dayjs(date, 'YYYY-MM-DD')
                    .add(this.formData.duration, 'month')
                    .subtract(1, 'd')
                    .format('YYYY-MM-DD');
            } else {
                if (!this.formData.endDate) {
                    return;
                }

                this.formData.duration = calculateDuration(
                    this.formData.endDate,
                    this.formData.startDate
                );
            }
        },
        async onSave() {
            try {
                this.$v.$touch();

                if (!this.isValid) {
                    return;
                }

                await this.saveContract(this.formData);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.formData.id
                        ? this.$t('createOrUpdateContract.editTitle')
                        : this.$t('createOrUpdateContract.createTitle'),
                    text: this.$t('createOrUpdateContract.saveSuccess')
                });

                this.onClose();
            } catch (error) {
                this.errorMessage = '';

                if (this.checkForServerFormErrors(error)) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.formData.id
                            ? this.$t('createOrUpdateContract.editTitle')
                            : this.$t('createOrUpdateContract.createTitle'),
                        text: this.$t('createOrUpdateContract.checkFormErrors')
                    });
                }
            }
        },
        async onClose() {
            setTimeout(this.setDefaultData, 100);
            this.$v.$reset();
            this.$emit('close');
        },
        onStartDatePickerInput() {
            this.$v.formData.startDate.$touch();
            this.clearServerErrors('startDate');
            this.onChangeDate();
            this.isStartDatePickerVisible = false;
        },
        onEndDatePickerInput() {
            this.$v.formData.endDate.$touch();
            this.clearServerErrors('endDate');
            this.onChangeDate();
            this.isEndDatePickerVisible = false;
        },
        closeWhenNoChanges() {
            const currentData = Object.entries(this.formData).toString();
            const previousData = Object.entries(this.formDataCopy).toString();

            if (previousData !== currentData) {
                return;
            }

            this.onClose();
        }
    }
};
</script>
